import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "../utils/cx.js";
import { Field } from "./Field.js";

export type InputSize = "sm" | "md" | "lg";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
  size?: InputSize;
  prefix?: ReactNode;
  suffix?: ReactNode;
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
}

const HEIGHT = { sm: "h-8", md: "h-9", lg: "h-10" } as const;

const BASE =
  "w-full rounded-md border border-input bg-transparent text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30";
const FOCUS = "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]";
const INVALID = "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30";

export function Input({
  size = "md",
  prefix,
  suffix,
  label,
  help,
  error,
  required,
  id,
  className,
  disabled,
  value,
  defaultValue,
  ...rest
}: InputProps) {
  const auto = useId();
  const inputId = id ?? auto;
  const invalid = error != null;

  let control: ReactNode;
  if (prefix != null || suffix != null) {
    control = (
      <div
        className={cn(
          BASE,
          HEIGHT[size],
          "flex items-center gap-2 px-3 focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
          invalid && "border-destructive focus-within:border-destructive focus-within:ring-destructive/30",
          disabled && "opacity-50",
          className,
        )}
      >
        {prefix != null && <span className="shrink-0 text-muted-foreground">{prefix}</span>}
        <input
          id={inputId}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className="h-full w-full bg-transparent outline-none placeholder:text-muted-foreground"
          value={value as string | undefined}
          defaultValue={defaultValue as string | undefined}
          {...rest}
        />
        {suffix != null && <span className="shrink-0 text-muted-foreground">{suffix}</span>}
      </div>
    );
  } else {
    control = (
      <input
        id={inputId}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(BASE, FOCUS, HEIGHT[size], "px-3 py-1", invalid && INVALID, className)}
        value={value as string | undefined}
        defaultValue={defaultValue as string | undefined}
        {...rest}
      />
    );
  }

  if (label != null || help != null || error != null) {
    return (
      <Field label={label} required={required} help={help} error={error} htmlFor={inputId}>
        {control}
      </Field>
    );
  }
  return control;
}
